import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight, CheckCircle2, Share2, Twitter, Linkedin, Facebook, QrCode, Zap, Users, TrendingUp, Star } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import QRCode from "react-qr-code";

export interface UseCaseData {
  slug: string;
  category: "who" | "usecase";
  badge: string;
  headline: string;
  subheadline: string;
  heroImg: string;
  accentFrom: string;
  accentTo: string;
  accentText: string;
  problem: { title: string; desc: string }[];
  benefits: { icon: string; title: string; desc: string }[];
  proof: string;
  usersCount: string;
  backLabel?: string;
}

const ALL_PAGES: Record<string, UseCaseData> = {
  brands: {
    slug: "brands", category: "who",
    badge: "For Brands",
    headline: "Your Brand Deserves a QR Code That Never Dies",
    subheadline: "Update every campaign in real-time. No reprints. No dead links. Just a living brand presence that follows your customers everywhere.",
    heroImg: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-violet-700", accentTo: "to-indigo-800", accentText: "text-violet-300",
    problem: [
      { title: "Dead QR codes damage brand trust", desc: "Every expired QR on printed material tells customers your brand isn't on top of things." },
      { title: "Reprints drain campaign budgets", desc: "Seasonal campaigns force you to reprint flyers, packaging and ads every quarter." },
      { title: "Zero insight from static codes", desc: "You're flying blind — no analytics on which campaign QR drives actual conversions." },
    ],
    benefits: [
      { icon: "🔄", title: "Update Any Campaign Instantly", desc: "Change the destination URL behind any QR without touching a single printed piece." },
      { icon: "📊", title: "Real-Time Campaign Analytics", desc: "See exactly which QR placement is driving the most scans, clicks, and conversions." },
      { icon: "🎨", title: "Fully Branded QR Profiles", desc: "Custom colours, logo, and brand voice — consistent across every market and channel." },
    ],
    proof: "Brands using CommonQR see 3× more engagement per campaign vs static QR tools.",
    usersCount: "3,200+",
  },
  businesses: {
    slug: "businesses", category: "who",
    badge: "For Businesses",
    headline: "One QR Code That Works for Your Entire Business",
    subheadline: "Hours. Menu. Reviews. Location. Booking. All behind a single living QR that customers always trust — and you update in seconds.",
    heroImg: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-indigo-700", accentTo: "to-sky-800", accentText: "text-sky-300",
    problem: [
      { title: "Customers arrive at the wrong time", desc: "Your hours changed but the old QR still points to outdated info — they walk away frustrated." },
      { title: "Multiple tools create chaos", desc: "Google listing, website, WhatsApp, booking page — customers can't figure out where to go." },
      { title: "Missed scans = missed revenue", desc: "Every untracked scan is a customer whose behaviour you can't learn from or follow up with." },
    ],
    benefits: [
      { icon: "🏪", title: "All Business Info in One Scan", desc: "Hours, location, menu, offers, contact, booking — everything behind one perfect QR." },
      { icon: "⚡", title: "Update Info in Seconds", desc: "Change hours, add promotions, or update your menu in 30 seconds from any device." },
      { icon: "📍", title: "Track Foot Traffic Sources", desc: "Know exactly which door, window, or card placement is driving the most walk-ins." },
    ],
    proof: "Businesses using CommonQR report 40% fewer 'wrong hours' complaints from customers.",
    usersCount: "4,100+",
  },
  products: {
    slug: "products", category: "who",
    badge: "For Products",
    headline: "Turn Every Product Into a 24/7 Sales & Support Agent",
    subheadline: "The moment a customer holds your product, your QR code links them to tutorials, reorders, reviews, and upsells — automatically.",
    heroImg: "https://images.unsplash.com/photo-1491553895911-0055eca6402d?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-rose-700", accentTo: "to-orange-700", accentText: "text-orange-300",
    problem: [
      { title: "Packaging has dead space", desc: "Customers open the box, find no way to re-engage — you lose the post-purchase moment forever." },
      { title: "Returns spike without guidance", desc: "Without easy access to tutorials and support, customers return products instead of learning them." },
      { title: "No post-purchase revenue", desc: "Every product sold without a QR is a missed opportunity to cross-sell, upsell, or earn a review." },
    ],
    benefits: [
      { icon: "📦", title: "Post-Purchase Engagement", desc: "Link buyers to setup guides, videos, warranty registration, and loyalty rewards instantly." },
      { icon: "⭐", title: "Drive 5-Star Reviews", desc: "Route happy customers directly to your review page with one scan at peak satisfaction." },
      { icon: "🔁", title: "Automated Reorder Path", desc: "Make reordering effortless — scan the box and buy again in two taps." },
    ],
    proof: "Products with CommonQR QR codes see 2.8× higher review rates and 35% more repeat purchases.",
    usersCount: "2,800+",
  },
  "local-businesses": {
    slug: "local-businesses", category: "who",
    badge: "For Local Businesses",
    headline: "Your Neighbourhood Customers Deserve Better Than a Static Flyer",
    subheadline: "Restaurant. Salon. Gym. Clinic. One QR on your door or table tells locals everything they need — and keeps it always fresh.",
    heroImg: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-orange-600", accentTo: "to-amber-700", accentText: "text-amber-300",
    problem: [
      { title: "Printed menus are costly and outdated", desc: "Every time prices or dishes change, you pay to reprint. That's money straight into the bin." },
      { title: "Customers can't find your info online", desc: "No unified digital presence means Google shows wrong hours, old menus, and no contact method." },
      { title: "No way to capture return visits", desc: "A walk-in customer leaves with no incentive, no contact, and no reason to come back." },
    ],
    benefits: [
      { icon: "🍽", title: "Digital Menu Always Up to Date", desc: "Change dishes, prices, and specials instantly. No reprinting, ever again." },
      { icon: "💬", title: "WhatsApp, Call, Book in One Tap", desc: "Customers scan and immediately see every way to contact or reserve a table." },
      { icon: "🎁", title: "Push Offers to Loyal Customers", desc: "Update your QR profile with today's deal — everyone who scanned yesterday sees it instantly." },
    ],
    proof: "Local businesses on CommonQR save an average of $900/year on reprinting costs.",
    usersCount: "5,600+",
  },
  celebrities: {
    slug: "celebrities", category: "who",
    badge: "For Celebrities & Influencers",
    headline: "One QR. Every Fan Touchpoint. Infinite Reach.",
    subheadline: "Merchandise. Social media. Exclusive content. Event tickets. Your fans scan once and enter your world completely.",
    heroImg: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-amber-600", accentTo: "to-yellow-600", accentText: "text-yellow-300",
    problem: [
      { title: "Fans struggle to find the real you", desc: "Fake accounts, outdated links, and scattered platforms confuse fans and dilute your brand." },
      { title: "Merch drop links expire", desc: "Every printed QR for a merch drop stops working after the campaign ends." },
      { title: "You can't measure fan engagement", desc: "Without analytics, you have no idea which content format or platform your fans prefer most." },
    ],
    benefits: [
      { icon: "⭐", title: "Official Verified Profile", desc: "A blue-tick verified CommonQR profile that fans know is 100% you — not an impersonator." },
      { icon: "🎵", title: "All Platforms in One Scan", desc: "YouTube, Spotify, Instagram, TikTok, merch store — organised and always accessible." },
      { icon: "📈", title: "Fan Engagement Analytics", desc: "See which platform your fans click most, which content drives most action, in real-time." },
    ],
    proof: "Celebrities on CommonQR grow cross-platform followers 2× faster than link-in-bio alternatives.",
    usersCount: "1,400+",
  },
  professionals: {
    slug: "professionals", category: "who",
    badge: "For Professionals",
    headline: "Your Business Card Is 10 Years Behind. Your QR Profile Isn't.",
    subheadline: "LinkedIn. Portfolio. Calendar booking. WhatsApp. Email. Share everything that matters in a single scan — and always stay current.",
    heroImg: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-emerald-700", accentTo: "to-teal-700", accentText: "text-emerald-300",
    problem: [
      { title: "Paper cards get lost immediately", desc: "Prospects pocket your card, forget it in a jacket, and never follow up — a dead lead every time." },
      { title: "Your info changes, cards don't", desc: "New phone number, new company, new LinkedIn — but thousands of old cards are already out there." },
      { title: "No way to know who's interested", desc: "With a paper card you never know who looked you up or when — zero follow-up intelligence." },
    ],
    benefits: [
      { icon: "📱", title: "Smart Digital Business Card", desc: "Print your CommonQR on your physical card. It links to your live profile — always updated." },
      { icon: "🔔", title: "Know When Someone Scans You", desc: "Get notified when prospects scan your QR. Reach out at exactly the right moment." },
      { icon: "📅", title: "One-Tap Meeting Booking", desc: "Calendly, Google Meet, LinkedIn — all right there. No email back-and-forth." },
    ],
    proof: "Professionals with CommonQR report 5× more follow-up conversations after networking events.",
    usersCount: "6,200+",
  },
  "business-card": {
    slug: "business-card", category: "usecase",
    badge: "Use Case: Business Cards",
    headline: "Your Business Card Should Open Doors, Not Collect Dust",
    subheadline: "A QR on your business card transforms a paper handshake into a living digital connection — portfolio, contact, socials, and booking in one scan.",
    heroImg: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-800", accentTo: "to-indigo-900", accentText: "text-indigo-300",
    problem: [
      { title: "Paper cards never get followed up", desc: "Most business cards end up in a drawer or bin within 72 hours. Your opportunity is gone." },
      { title: "Can't update when your info changes", desc: "New job, new phone, new LinkedIn URL — now thousands of old cards are wrong." },
      { title: "You have no idea who's interested", desc: "Paper cards give zero signal about who read it, visited your site, or wants to connect." },
    ],
    benefits: [
      { icon: "🔗", title: "Everything on One Profile", desc: "LinkedIn, portfolio, phone, WhatsApp, email — all accessible from one QR on your card." },
      { icon: "🔄", title: "Update Anytime, Reprint Never", desc: "Changed jobs? New portfolio piece? Update your profile in 30 seconds — same QR, forever." },
      { icon: "📊", title: "Know Exactly Who Scanned", desc: "See analytics on when, where, and how often your card is scanned. Follow up with precision." },
    ],
    proof: "Professionals with CommonQR on their business card get 5× more follow-up conversations.",
    usersCount: "8,200+",
  },
  "product-packaging": {
    slug: "product-packaging", category: "usecase",
    badge: "Use Case: Product Packaging",
    headline: "Your Packaging Is the Most Underused Sales Tool You Own",
    subheadline: "A QR code on your perfume box, cosmetic label, or consumer product turns unboxing into an immersive brand journey and repeat purchase trigger.",
    heroImg: "https://images.unsplash.com/photo-1556228578-8c89e6adf883?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-rose-800", accentTo: "to-pink-900", accentText: "text-rose-300",
    problem: [
      { title: "Packaging ends at unboxing", desc: "Customers open the box, use the product, and never engage with your brand again." },
      { title: "Returns spike without guidance", desc: "Without quick access to tutorials, customers return products they could have learned to love." },
      { title: "Competitors steal your customer next", desc: "Without a reorder link, your customer's next purchase goes to Amazon or a competitor." },
    ],
    benefits: [
      { icon: "🎬", title: "Tutorials & How-To Videos", desc: "Scan the box to instantly access step-by-step setup guides, usage tips, and demos." },
      { icon: "⭐", title: "Drive Reviews at Peak Joy", desc: "Catch customers at the excitement moment — redirect to your review page immediately." },
      { icon: "🛒", title: "One-Tap Reorder Button", desc: "When the product runs out, customers scan and reorder in two taps — no friction." },
    ],
    proof: "Brands with CommonQR on packaging see 2.8× more repeat purchases and 60% fewer returns.",
    usersCount: "2,400+",
  },
  storefront: {
    slug: "storefront", category: "usecase",
    badge: "Use Case: Storefront",
    headline: "Every Customer Who Walks Past Is a Missed Opportunity Without a QR",
    subheadline: "A CommonQR standee at your billing counter builds instant trust, drives repeat visits, and turns every transaction into a relationship.",
    heroImg: "https://images.unsplash.com/photo-1481437156560-3205f6a55735?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-800", accentTo: "to-slate-900", accentText: "text-slate-300",
    problem: [
      { title: "Customers leave without a next step", desc: "They pay, smile, and walk out — with no digital connection to your store." },
      { title: "No way to share your offers", desc: "Great deals and loyalty programmes go unnoticed because customers don't know where to look." },
      { title: "Reviews are left unclaimed", desc: "Happy customers don't leave Google reviews because there's no easy path to do so in the moment." },
    ],
    benefits: [
      { icon: "🏪", title: "Trust Badge at Checkout", desc: "A verified QR standee on your counter shows customers your business is professional and transparent." },
      { icon: "📲", title: "Instant Reviews & Follow", desc: "One scan routes customers to Google, Instagram, or WhatsApp — right at peak satisfaction." },
      { icon: "🎁", title: "Loyalty Offers in One Tap", desc: "Update today's deal on your QR profile and every customer who scans sees it immediately." },
    ],
    proof: "Stores with a CommonQR standee at checkout see 3× more Google reviews within 30 days.",
    usersCount: "3,700+",
  },
  "restaurant-menu": {
    slug: "restaurant-menu", category: "usecase",
    badge: "Use Case: Restaurant Menu",
    headline: "The Days of Reprinting Your Menu Are Over",
    subheadline: "Update prices, specials, and dishes in real time. Customers scan your table QR and always see your current menu — with no printing costs ever again.",
    heroImg: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-orange-700", accentTo: "to-red-800", accentText: "text-orange-300",
    problem: [
      { title: "Printing costs drain your margins", desc: "New dishes, seasonal specials, price changes — every update costs you in print and time." },
      { title: "Laminated menus feel outdated", desc: "Savvy diners expect digital experiences. A dated laminated card signals poor attention to quality." },
      { title: "Lost upsell opportunities", desc: "Static menus can't suggest wine pairings, specials, or high-margin add-ons dynamically." },
    ],
    benefits: [
      { icon: "📋", title: "Update Menu Instantly", desc: "Change a price, add a dish, or flag a sold-out item in 30 seconds from your phone." },
      { icon: "🍷", title: "Dynamic Upsell Suggestions", desc: "Update your profile with today's specials and wine pairings — always front and centre." },
      { icon: "📲", title: "Orders, Reservations & Reviews", desc: "Add WhatsApp ordering, booking links, and review requests all on one scannable profile." },
    ],
    proof: "Restaurants with CommonQR save an average of $1,400/year on menu printing costs alone.",
    usersCount: "4,900+",
  },
  "event-pass": {
    slug: "event-pass", category: "usecase",
    badge: "Use Case: Event Pass",
    headline: "Your Event Pass Is the First Impression That Sets the Tone",
    subheadline: "Attendees scan their badge QR to access the agenda, venue maps, speaker bios, and networking profiles — all instantly updated by your team.",
    heroImg: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-indigo-700", accentTo: "to-violet-800", accentText: "text-violet-300",
    problem: [
      { title: "Printed agendas are obsolete before the event", desc: "Speaker dropped out? Room changed? Attendees have outdated info and no way to know." },
      { title: "Networking is inefficient at events", desc: "Exchanging business cards at events is slow, lossy, and leads to zero follow-up." },
      { title: "No post-event engagement path", desc: "Once the event ends, your attendees have no easy way to stay connected to your brand." },
    ],
    benefits: [
      { icon: "📅", title: "Live Agenda Updates", desc: "Change speakers, rooms, or times — every attendee's QR instantly reflects the update." },
      { icon: "🤝", title: "Instant Attendee Networking", desc: "Each attendee has a scannable profile card — connect, follow, and engage in one tap." },
      { icon: "📧", title: "Post-Event Engagement", desc: "Update your QR after the event with recordings, slides, and next event details." },
    ],
    proof: "Events using CommonQR passes report 4× more post-event engagement from attendees.",
    usersCount: "1,900+",
  },
  "social-hub": {
    slug: "social-hub", category: "usecase",
    badge: "Use Case: Social Hub",
    headline: "Stop Sending Followers to a Bio Link That Looks Like 2018",
    subheadline: "Your CommonQR profile beautifully organises every social platform, store, video, and contact link — designed to convert browsers into followers instantly.",
    heroImg: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-pink-700", accentTo: "to-rose-800", accentText: "text-pink-300",
    problem: [
      { title: "Linktree feels generic and off-brand", desc: "Your followers can smell a template link page from a mile away. It cheapens your brand." },
      { title: "Followers can't find all your platforms", desc: "Content spread across TikTok, YouTube, Instagram, and Spotify — fans miss most of it." },
      { title: "No analytics on which links drive action", desc: "You're posting everywhere but have no idea which platform your audience actually prefers." },
    ],
    benefits: [
      { icon: "🎨", title: "Fully Branded Profile Page", desc: "Custom design, your colours, your voice — not a generic template that looks like everyone else." },
      { icon: "📱", title: "All Platforms Organised", desc: "Instagram, TikTok, YouTube, Spotify, Linktree-killer — one scroll, one tap, done." },
      { icon: "📈", title: "Click Analytics Per Link", desc: "See exactly which links your audience clicks most — and double down on what works." },
    ],
    proof: "Social creators on CommonQR see 2.4× higher link-click rates vs Linktree and Beacons.",
    usersCount: "7,300+",
  },
  "ad-campaign": {
    slug: "ad-campaign", category: "usecase",
    badge: "Use Case: Ad Campaign",
    headline: "Every Print Ad Without a Dynamic QR Is Wasting Half Its Budget",
    subheadline: "A CommonQR on your newspaper ad, flyer, or billboard tracks every scan, lets you change the destination without reprinting, and builds a retargetable audience.",
    heroImg: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-700", accentTo: "to-blue-900", accentText: "text-blue-300",
    problem: [
      { title: "Print ads can't be measured", desc: "You spend thousands on newspaper or flyer campaigns with zero visibility on how many people engaged." },
      { title: "Static QR codes kill mid-campaign pivots", desc: "If your landing page changes or the offer expires, every printed QR becomes a dead end." },
      { title: "No retargeting from offline ads", desc: "Anyone who scanned your offline QR is forever lost — you can't follow up with them digitally." },
    ],
    benefits: [
      { icon: "📊", title: "Track Every Scan in Real Time", desc: "See scan counts, peak times, locations, and device types for every campaign QR code." },
      { icon: "🔄", title: "Change Destination Without Reprinting", desc: "Update your campaign landing page URL instantly — the printed QR never needs to change." },
      { icon: "🎯", title: "Retarget Offline Scanners Online", desc: "Reach people who scanned your print ad with follow-up digital ads — seamless retargeting." },
    ],
    proof: "Advertisers using CommonQR report 3.5× better ROI tracking on print campaigns.",
    usersCount: "1,600+",
  },
  "commercial-invoice": {
    slug: "commercial-invoice", category: "usecase",
    badge: "Use Case: Commercial Invoice",
    headline: "Turn Your Invoice Into a Trusted, Scannable Business Identity",
    subheadline: "A CommonQR on your invoice lets clients verify your business, pay instantly, review terms, and contact support — before the invoice even goes to accounts.",
    heroImg: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-700", accentTo: "to-emerald-900", accentText: "text-emerald-300",
    problem: [
      { title: "Clients question invoice legitimacy", desc: "Fraud is rampant. Clients delay payment while verifying who sent the invoice." },
      { title: "Payment is a friction-filled process", desc: "Clients need to find your bank details, switch apps, and manually type amounts. Delays are common." },
      { title: "No professional digital presence on documents", desc: "A plain PDF invoice looks the same as a freelancer's and undermines your brand authority." },
    ],
    benefits: [
      { icon: "✅", title: "Verified Business Identity", desc: "Your QR links to your verified CommonQR profile — clients instantly confirm it's a legitimate business." },
      { icon: "💳", title: "One-Tap Payment Link", desc: "Include your payment link directly on the profile — clients pay in seconds, not days." },
      { icon: "📄", title: "Terms & Support Instantly Accessible", desc: "Link to your terms, refund policy, and support WhatsApp — zero friction, maximum trust." },
    ],
    proof: "Businesses with QR on invoices report 40% faster payment turnaround from clients.",
    usersCount: "1,200+",
  },
  "highway-hoarding": {
    slug: "highway-hoarding", category: "usecase",
    badge: "Use Case: Highway Hoarding",
    headline: "Your Highway Billboard Can Now Have a Measurable Digital Follow-Through",
    subheadline: "A bold QR on your outdoor hoarding turns a 3-second glance into a lasting digital connection — trackable, updatable, and always live.",
    heroImg: "https://images.unsplash.com/photo-1568792923760-d70635a89fdc?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-800", accentTo: "to-sky-900", accentText: "text-sky-300",
    problem: [
      { title: "Outdoor ads have zero ROI visibility", desc: "You spend lakhs on a highway hoarding but have no way to know if anyone acted on it." },
      { title: "Static content misses time-sensitive offers", desc: "A sale ends but your hoarding still shows it. The QR points to an expired page." },
      { title: "Driver scans, then forgets you instantly", desc: "No easy digital path means that moment of interest is lost within seconds." },
    ],
    benefits: [
      { icon: "🛣", title: "Scan at Traffic Lights", desc: "Drivers who stop at a signal have 45+ seconds to scan your QR and land on your profile." },
      { icon: "📊", title: "Measure Hoarding Performance", desc: "Track scan counts per billboard, per region, per time period — true outdoor ROI." },
      { icon: "🔄", title: "Update Offer Without Changing Board", desc: "Run a new offer every month without touching the printed hoarding — dynamic QR does the rest." },
    ],
    proof: "Outdoor advertisers using CommonQR see up to 8× more trackable leads vs unlinked hoardings.",
    usersCount: "680+",
  },
  "retail-carry-bag": {
    slug: "retail-carry-bag", category: "usecase",
    badge: "Use Case: Retail Carry Bag",
    headline: "Your Carry Bag Walks Into 500 Homes. Does It Bring Them Back?",
    subheadline: "A QR on your retail carry bag keeps customers connected after they leave — loyalty rewards, new arrivals, offers, and easy reordering in one scan.",
    heroImg: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-rose-700", accentTo: "to-pink-800", accentText: "text-rose-300",
    problem: [
      { title: "Carry bags are branding that disappears", desc: "Your bag gets used once and discarded — the branding investment is wasted." },
      { title: "No post-purchase engagement path", desc: "Customers leave with your bag and have no easy way to come back for new arrivals." },
      { title: "Competitors target your customers next", desc: "Without a loyalty hook, your customer's next purchase goes to whoever reaches them first." },
    ],
    benefits: [
      { icon: "🛍", title: "Loyalty & Offers in One Scan", desc: "Customers scan the bag at home to access loyalty points, exclusive offers, and new arrivals." },
      { icon: "📲", title: "WhatsApp Community Invite", desc: "Redirect bag scanners to your WhatsApp community — owned audience, zero advertising cost." },
      { icon: "🔁", title: "Repeat Purchase Trigger", desc: "Push seasonal sale reminders to customers who scanned your bag — they're already warm." },
    ],
    proof: "Retailers with CommonQR on carry bags see 2.2× higher repeat customer rate within 90 days.",
    usersCount: "920+",
  },
  "office-envelope": {
    slug: "office-envelope", category: "usecase",
    badge: "Use Case: Office Envelope",
    headline: "Your Corporate Envelope Should Say More Than Just Your Address",
    subheadline: "A QR code on your office envelope transforms every letter and parcel into a professional brand touchpoint — LinkedIn, website, direct contact in one scan.",
    heroImg: "https://images.unsplash.com/photo-1568093858174-c57e6e1804f4?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-slate-700", accentTo: "to-indigo-900", accentText: "text-indigo-300",
    problem: [
      { title: "Corporate mail feels cold and anonymous", desc: "Recipients get an envelope with no way to quickly verify or learn about your business." },
      { title: "Business relationships start blind", desc: "No digital path from physical mail means recipients search manually — and often don't bother." },
      { title: "Missed opportunity at first impression", desc: "Every envelope is a first impression moment. Without QR, that moment is underutilised." },
    ],
    benefits: [
      { icon: "🏢", title: "Instant Business Verification", desc: "Recipients scan the envelope and land on your verified profile — trust established immediately." },
      { icon: "🔗", title: "Full Company Digital Presence", desc: "Website, LinkedIn, team contacts, and brochure — all accessible before they even open the letter." },
      { icon: "📞", title: "Direct Communication Channel", desc: "One tap to email, WhatsApp, or book a call — no friction, no directory searching." },
    ],
    proof: "Companies with QR on envelopes report 60% faster response rates from business recipients.",
    usersCount: "740+",
  },
  "ecommerce-parcel": {
    slug: "ecommerce-parcel", category: "usecase",
    badge: "Use Case: E-Commerce Parcel",
    headline: "Your Parcel Label Is Your Last Chance to Win a Loyal Customer",
    subheadline: "A CommonQR on your delivery box or label turns an ordinary unboxing into a wow moment — tutorials, reviews, loyalty rewards, and reorder links instantly accessible.",
    heroImg: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=90",
    accentFrom: "from-emerald-700", accentTo: "to-teal-800", accentText: "text-emerald-300",
    problem: [
      { title: "Unboxing ends with the box", desc: "Customers open the parcel, use the product, and forget about your brand until the next paid ad." },
      { title: "Returns spike without post-purchase support", desc: "Without easy access to tutorials or support, customers return instead of reaching out to you." },
      { title: "No path from purchase to review", desc: "Most e-commerce businesses never collect reviews because there's no frictionless way to ask." },
    ],
    benefits: [
      { icon: "📦", title: "Unboxing Experience QR", desc: "Scan the parcel for a branded welcome message, tutorial video, and care instructions." },
      { icon: "⭐", title: "Review Redirect at Peak Delight", desc: "Catch customers at the excitement moment — route them straight to your review page." },
      { icon: "🎁", title: "Loyalty Reward on First Scan", desc: "Reward customers who scan with an exclusive discount on their next order — drive repeat purchase." },
    ],
    proof: "E-commerce brands with CommonQR on parcels see 3.1× more organic reviews and 50% lower return rates.",
    usersCount: "3,100+",
  },
};

function ShareButtons({ title, url }: { title: string; url: string }) {
  const text = encodeURIComponent(`${title} — Check out CommonQR, the smart QR profile platform!`);
  const encodedUrl = encodeURIComponent(url);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${title} — CommonQR`, url });
      } catch (_) {}
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-semibold text-muted-foreground mr-1">Share with friends who need this:</span>
      <button
        onClick={handleNativeShare}
        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full text-xs font-medium transition-all"
      >
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      <a
        href={`https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/30 text-sky-300 rounded-full text-xs font-medium transition-all"
      >
        <Twitter className="w-3.5 h-3.5" /> Twitter / X
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 rounded-full text-xs font-medium transition-all"
      >
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
      </a>
      <a
        href={`https://wa.me/?text=${text}%20${encodedUrl}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 rounded-full text-xs font-medium transition-all"
      >
        <span className="text-xs">📲</span> WhatsApp
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 bg-blue-700/20 hover:bg-blue-700/30 border border-blue-600/30 text-blue-300 rounded-full text-xs font-medium transition-all"
      >
        <Facebook className="w-3.5 h-3.5" /> Facebook
      </a>
    </div>
  );
}

function FomoCta({ accentFrom, accentTo }: { accentFrom: string; accentTo: string }) {
  const [username, setUsername] = useState("");
  const preview = username.trim().toLowerCase().replace(/\s+/g, "-") || "yourname";
  const previewUrl = `https://commonqr.com/${preview}`;

  return (
    <section className={`relative bg-gradient-to-br ${accentFrom} ${accentTo} py-20 overflow-hidden`}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold mb-6">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-400" />
          </span>
          Your username might already be taken
        </div>

        <h2 className="text-3xl lg:text-[42px] font-bold text-white tracking-tight mb-3 leading-tight">
          Claim <span className="text-white/60">commonqr.com/</span><span className="underline decoration-wavy decoration-yellow-400">yourname</span>
          <br />before someone else does.
        </h2>
        <p className="text-base text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
          Thousands of profiles are created every week. Premium usernames — your name, your brand, your city — are going fast. Secure yours now for free.
        </p>

        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-6 max-w-md mx-auto">
          <label className="text-xs font-semibold text-white/60 tracking-widest uppercase block mb-2">Check your username</label>
          <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 mb-3">
            <span className="text-white/50 text-sm font-mono">commonqr.com/</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="yourname"
              className="flex-1 bg-transparent text-white placeholder:text-white/30 text-sm font-mono focus:outline-none"
            />
          </div>
          <div className="bg-white/5 rounded-lg p-2 mb-4 flex justify-center">
            <QRCode value={previewUrl} size={80} fgColor="#ffffff" bgColor="transparent" />
          </div>
          <Link
            href={`/signup?username=${encodeURIComponent(username.trim().toLowerCase().replace(/\s+/g, "-"))}`}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-white text-slate-900 hover:bg-slate-100 rounded-xl text-sm font-bold transition-all shadow-lg"
          >
            Claim This Profile — It's Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="text-xs text-white/40">🔒 Free forever. No credit card. Your profile is live in 60 seconds.</p>
      </div>
    </section>
  );
}

export function UseCasePage({ slug }: { slug: string }) {
  const data = ALL_PAGES[slug];

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground">
        <p className="text-2xl font-bold mb-4">Page not found</p>
        <Link href="/" className="text-indigo-400 hover:text-indigo-300">← Back to CommonQR</Link>
      </div>
    );
  }

  const pageUrl = `https://commonqr.com/${data.slug}`;
  const metaDescription = (data as any).metaDescription || data.subheadline;

  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <Helmet>
        <title>{data.headline} — CommonQR</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${data.headline} — CommonQR`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={data.heroImg} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Navbar />

      {/* HERO — Full screen */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img
          src={data.heroImg}
          alt={data.badge}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className={`absolute inset-0 bg-gradient-to-br ${data.accentFrom} ${data.accentTo} opacity-80`} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-24">
          <Link href="/" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to CommonQR
          </Link>

          <div className={`inline-block px-3 py-1 rounded-full bg-white/15 border border-white/20 ${data.accentText} text-xs font-bold tracking-[0.1em] uppercase mb-5`}>
            {data.badge}
          </div>

          <h1 className="text-[38px] sm:text-5xl lg:text-[60px] font-bold text-white leading-tight tracking-tight mb-5 max-w-3xl">
            {data.headline}
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mb-8 leading-relaxed">
            {data.subheadline}
          </p>

          <div className="flex flex-wrap gap-3 items-center mb-10">
            <Link href="/signup" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-slate-900 hover:bg-slate-100 rounded-full text-sm font-bold transition-all shadow-xl">
              Create My Free QR Profile <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              {data.usersCount} using this right now
            </div>
          </div>

          {/* Social proof mini-bar */}
          <div className="flex flex-wrap gap-4">
            {[
              { icon: "⚡", text: "30-second setup" },
              { icon: "🆓", text: "Free forever plan" },
              { icon: "🔒", text: "No credit card" },
              { icon: "🔄", text: "Update anytime" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-1.5 text-white/50 text-xs">
                <span>{item.icon}</span>{item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-background py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <p className="text-[11px] font-semibold text-rose-400 tracking-[0.1em] uppercase mb-3">The Problem</p>
            <h2 className="text-2xl lg:text-[36px] font-bold text-foreground tracking-tight leading-tight">
              Why most people in your position are leaving money on the table
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {data.problem.map((p, i) => (
              <div key={i} className="bg-rose-950/40 border border-rose-900/40 rounded-2xl p-6">
                <div className="w-8 h-8 bg-rose-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-rose-400 font-bold text-sm">{i + 1}</span>
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{p.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION — BENEFITS */}
      <section className={`relative bg-gradient-to-br ${data.accentFrom}/30 ${data.accentTo}/30 bg-slate-900 py-20`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <p className="text-[11px] font-semibold text-indigo-400 tracking-[0.1em] uppercase mb-3">The CommonQR Advantage</p>
            <h2 className="text-2xl lg:text-[36px] font-bold text-white tracking-tight leading-tight">
              Here's what changes the moment you create your profile
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {data.benefits.map((b, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all">
                <div className="text-3xl mb-4">{b.icon}</div>
                <h3 className="text-base font-bold text-white mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BANNER */}
      <section className="bg-background border-t border-border/10 py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            {[
              { num: data.usersCount, label: "profiles in this category" },
              { num: "500,000+", label: "total scans served" },
              { num: "30 sec", label: "average setup time" },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl font-bold text-foreground mb-1">{stat.num}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-white/5 border border-border/20 rounded-2xl p-5">
            <div className="flex gap-1 justify-center mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-center text-muted-foreground text-sm italic max-w-2xl mx-auto">"{data.proof}"</p>
          </div>
        </div>
      </section>

      {/* FOMO CTA */}
      <FomoCta accentFrom={data.accentFrom} accentTo={data.accentTo} />

      {/* SHARE SECTION */}
      <section className="bg-background py-14 border-t border-border/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h3 className="text-lg font-bold text-foreground mb-5">Know someone who would benefit from this?</h3>
          <ShareButtons title={data.badge} url={pageUrl} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-card text-muted-foreground py-8 px-6 border-t border-border/10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link href="/" className="flex items-center gap-2">
            <QrCode size={16} className="text-indigo-400" />
            <span className="font-semibold text-sm text-foreground tracking-tight">CommonQR</span>
          </Link>
          <Link href="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to Home
          </Link>
          <p className="text-xs">&copy; {new Date().getFullYear()} CommonQR.</p>
        </div>
      </footer>
    </div>
  );
}

export default UseCasePage;
